// The size of a surface, in the two terms its reported measurement can honestly be read in: what
// shape it is, and how big it is on the wall.
//
// UE reports a frame's width and height as **world extents** — Unreal units, whatever the field
// names suggest — so the pair says nothing about how many pixels an image for it should have. That
// is only true where the art happens to be drawn at one unit per pixel.
//
// The panel printed the pair verbatim until 2026-09-04, and `1200 × 675` beside an upload control
// cannot read as anything but a pixel target. An author sizing a JPEG to it was following a
// coincidence. What the measurement really supports is the ratio, which is what an image has to
// match, and the physical size, which is how this frame compares to the one next to it.
import type { TFunction } from 'react-i18next';

// Ratios worth naming, landscape-first. Short on purpose: every entry is a shape an author might
// deliberately author to, and a longer list starts snapping measurements onto ratios nobody was
// aiming for. Portrait surfaces are matched against the same entries reversed.
const NAMED_RATIOS: [number, number][] = [
  [1, 1],
  [5, 4],
  [4, 3],
  [3, 2],
  [16, 10],
  [16, 9],
  [2, 1],
  [21, 9],
];

// How far a measurement may sit from a named ratio and still be called it.
//
// 1.5% is under half the gap between the closest neighbours on the list (16:10 and 3:2 are 4%
// apart), so no measurement can be within tolerance of two of them — and it is wide enough that a
// frame really built to 16:9 survives the centimetre of rounding UE applies before reporting.
//
// **That second half makes this number one end of a cross-repo pair.** It is chosen against the
// resolution of `FMath::RoundToInt` in `ResolveSpotDesignSize`, which carries a comment naming
// this file back (UE cs:15084):
// UeProject/Plugins/Mytaverse/MytaMapEditor/Source/MytaMapEditor/Private/MytaMapEditorLayout.cpp
//
// Move that rounding to a float, or coarsen its step, and this chip starts printing decimals for
// walls an author built to a named ratio — silently, with every test on both sides still green,
// because no build covers the two repositories together.
const TOLERANCE = 0.015;

// One unit is one centimetre — the engine default, which this project has left alone.
//
// Cited rather than asserted, because it is a property of the project's *configuration* and not of
// any code either side of this: `WorldToMeters` is set by no `Config/*.ini` and no plugin, and the
// authority for that is the frame-measurement section of
// `UeProject/Plugins/Mytaverse/MytaMapEditor/README.md`. A level that one day ships its own
// `WorldSettings` override would invalidate this constant without touching a line of it, so that
// sentence is what to re-read if the metres ever look wrong.
const UNREAL_UNITS_PER_METRE = 100;

/**
 * Whether a reported extent is a measurement this module can describe.
 *
 * @param value - One of the reported pair.
 * @returns True for a positive, finite number.
 */
const isMeasured = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/**
 * Names the shape of a surface.
 *
 * @param width - Reported width, in Unreal units.
 * @param height - Reported height, in Unreal units.
 * @returns A ratio such as `16:9`, or `1.85:1` for a shape no named ratio is close to. Null when
 * there is no measurement to describe — which is not the same as a square, so callers must draw
 * nothing rather than assume one.
 */
export const describeFrameAspect = (
  width: number | undefined,
  height: number | undefined,
): string | null => {
  if (!isMeasured(width) || !isMeasured(height)) {
    return null;
  }

  const isLandscape = width >= height;
  // Compared in one orientation and reversed on the way out, so the list needs no portrait entries
  // and a 9:16 screen cannot be described as 16:9.
  const ratio = isLandscape ? width / height : height / width;

  const named = NAMED_RATIOS.find(
    ([namedWidth, namedHeight]) =>
      Math.abs(ratio / (namedWidth / namedHeight) - 1) <= TOLERANCE,
  );

  if (named) {
    const [namedWidth, namedHeight] = named;

    return isLandscape
      ? `${namedWidth}:${namedHeight}`
      : `${namedHeight}:${namedWidth}`;
  }

  // Two decimals rather than a reduced fraction: the extents are rounded centimetres, so reducing
  // them gives something like 1327:718 — arithmetically exact and useless to read.
  const decimal = ratio.toFixed(2);

  return isLandscape ? `${decimal}:1` : `1:${decimal}`;
};

/**
 * How big the surface is on the wall.
 *
 * One decimal and no more, because that is the precision the field actually carries: UE rounds each
 * extent to a whole centimetre before reporting it, so a second decimal would promise a millimetre
 * the measurement never had.
 *
 * The number already includes the actor's own scale, which is the useful answer — a frame an artist
 * stretched reads at the size it is on the wall rather than at its source asset's size.
 *
 * @param width - Reported width, in Unreal units.
 * @param height - Reported height, in Unreal units.
 * @param translate - i18next `t`, passed in so this stays a plain function.
 * @returns The extent in metres, or null when there is no measurement.
 */
export const describeFrameExtent = (
  width: number | undefined,
  height: number | undefined,
  translate: TFunction,
): string | null => {
  if (!isMeasured(width) || !isMeasured(height)) {
    return null;
  }

  const toMetres = (units: number) =>
    (units / UNREAL_UNITS_PER_METRE).toFixed(1);

  return translate(
    'editorMode.surfaceExtentMetres',
    '{{width}} × {{height}} m',
    {
      width: toMetres(width),
      height: toMetres(height),
    },
  );
};

/**
 * The whole of what is known about a surface's size, as the inspector's chip says it.
 *
 * The aspect leads because it is the part an author acts on; the extent follows because it is the
 * part that tells them which frame in the room they are looking at.
 *
 * @param width - Reported width, in Unreal units.
 * @param height - Reported height, in Unreal units.
 * @param translate - i18next `t`.
 * @returns The chip's text, or null when the build reported no measurement for this surface — in
 * which case the chip is absent rather than empty. A rotated frame is one such case: UE reports no
 * pair unless the frame is axis-aligned, which is also what makes the metres trustworthy when they
 * are there, since the box it measured is then the frame itself.
 */
export const describeFrameSize = (
  width: number | undefined,
  height: number | undefined,
  translate: TFunction,
): string | null => {
  const aspect = describeFrameAspect(width, height);

  if (!aspect) {
    return null;
  }

  const extent = describeFrameExtent(width, height, translate);

  return extent ? `${aspect} · ${extent}` : aspect;
};
